import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Widget } from 'api/dashboard';
import { getApiGroupedFilterTransformer } from 'components/pages/api-grouped-filter-transformer';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { RootState } from 'data';
import { FieldsStore, TaskField } from 'stores/fields';
import { QueryCombinerType } from '../tasks/combiner';
import { excludedAllTasksFields } from '../tasks/constants';
import { GroupedFilter } from '../tasks/grouped-filter-popover';
import { useFilterFields } from './use-filter-fields';

interface Props {
  widget: Widget;
  defaultGroup: FilterGroupType;
}
export const useWidgetFilter = ({
  widget,
  defaultGroup,
}: Props): GroupedFilter => {
  const { data: projectFields, isLoading } = useFilterFields();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const taskFields: TaskField[] = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedAllTasksFields.includes(fieldName),
      ),
    [fieldsStore],
  );
  const groups: GroupFieldsMeta = useMemo(
    () => [
      {
        id: FilterGroupType.Project,
        fields: keyBy(projectFields, 'fieldName'),
      },
      {
        id: FilterGroupType.Task,
        fields: keyBy(taskFields, 'fieldName'),
      },
    ],
    [projectFields, taskFields],
  );
  const transformer = useMemo(
    () => getApiGroupedFilterTransformer(groups, defaultGroup),
    [defaultGroup, groups],
  );

  return useMemo(
    () =>
      isLoading
        ? { groupCriteria: [], type: QueryCombinerType.All, defaultGroup }
        : transformer.from(widget.simpleSearchFilter),
    [isLoading, defaultGroup, transformer, widget.simpleSearchFilter],
  );
};
